// Shared "pending" / "running" / "closed" classification for Program/Component/Goal
// (and any similar) status lists — driven by `lookup_values.status_group` (admin-
// editable via Admin > Lookup Values), never hardcoded status strings in JS. Real
// status values already drift outside migrations (e.g. "Closed – Not Invoiced" was
// added ad hoc via that same editor), so classification has to be data-driven too.

// lookupValues: array of {value, status_group, ...} from GET /lookup-values/?category=X
export function buildStatusGroupMap(lookupValues) {
  const map = {};
  (lookupValues || []).forEach(lv => { map[lv.value] = lv.status_group || null; });
  return map;
}

const GROUP_RANK = { pending: 0, running: 1 };

// Splits `rows` into { active, closed } using `statusKey` (a field name on each row)
// and `groupMap` (status value -> 'pending'|'running'|'closed'|null, from
// buildStatusGroupMap). A status with no group set yet (not yet classified in Lookup
// Values, or a genuinely new one) defaults into "active" — never silently hides real
// data behind a classification nobody has set. Each bucket sorts by `dateKey`
// descending (most recent first); active additionally sorts Pending before Running.
export function splitByStatusGroup(rows, { statusKey, groupMap, dateKey }) {
  const active = [];
  const closed = [];
  (rows || []).forEach(row => {
    const group = groupMap[row[statusKey]];
    if (group === 'closed') closed.push(row);
    else active.push(row);
  });

  const byDateDesc = (a, b) => {
    const ad = a[dateKey], bd = b[dateKey];
    if (!ad && !bd) return 0;
    if (!ad) return 1;
    if (!bd) return -1;
    return new Date(bd) - new Date(ad);
  };

  active.sort((a, b) => {
    const ra = GROUP_RANK[groupMap[a[statusKey]]] ?? 0;
    const rb = GROUP_RANK[groupMap[b[statusKey]]] ?? 0;
    if (ra !== rb) return ra - rb;
    return byDateDesc(a, b);
  });
  closed.sort(byDateDesc);

  return { active, closed };
}

// Program-dropdown-shaped {options, closedOptions} for SearchableSelect's own
// grouping — a thin wrapper over splitByStatusGroup so every Program picker in
// the app (there are many — see main CLAUDE.md's "Nest closed programs in
// dropdowns" note) builds the exact same split instead of each reimplementing
// it. `programs`: rows with `id`/`program_name`/`status`. `groupMap`: from
// buildStatusGroupMap(GET /lookup-values/?category=program_status). Option
// `value`s are always strings — several existing call sites do their own
// `String(row.program_id) === filterProgram` comparison downstream of
// SearchableSelect's own (already string-loose) selection, so a raw numeric
// id here would silently break those.
export function buildProgramOptionGroups(programs, groupMap) {
  const { active, closed } = splitByStatusGroup(programs, { statusKey: 'status', groupMap, dateKey: 'flight_date_end' });
  const toOption = p => ({ value: String(p.id), label: p.program_name || `Program ${p.id}` });
  return { options: active.map(toOption), closedOptions: closed.map(toOption) };
}

// Component/Goal/Delivery-Destination pickers have no status_group of their
// own to check — "closed" is inherited from their OWNING program's status.
// closedProgramIdSet() turns the program list into a Set of just the closed
// ones' ids, cheap to build once per render and pass to every picker below.
export function closedProgramIdSet(programs, groupMap) {
  const ids = new Set();
  (programs || []).forEach(p => { if (groupMap[p.status] === 'closed') ids.add(p.id); });
  return ids;
}

// Generic {options, closedOptions} builder for any row type whose "closed"
// status is inherited from an owning program rather than a status field on
// the row itself. `programIdOf(row)` returns that row's owning program id
// (or null/undefined if it can't be resolved, in which case the row is
// always treated as active — never silently hidden by a lookup gap).
// `toOption(row)` builds the {value,label} pair, so callers with a custom
// label format (e.g. a client-prefixed name) can still use this directly
// instead of hand-rolling the split.
export function buildOwningProgramOptionGroups(rows, programIdOf, closedProgramIds, toOption) {
  const options = [];
  const closedOptions = [];
  (rows || []).forEach(row => {
    const pid = programIdOf(row);
    (pid != null && closedProgramIds.has(pid) ? closedOptions : options).push(toOption(row));
  });
  return { options, closedOptions };
}

// Convenience wrapper for the common Component-picker case — Component has
// a direct program_id, and the standard label is component_name.
export function buildComponentOptionGroups(components, closedProgramIds) {
  return buildOwningProgramOptionGroups(
    components, c => c.program_id, closedProgramIds,
    c => ({ value: String(c.id), label: c.component_name || `Component ${c.id}` }),
  );
}

// Convenience wrapper for the common Goal-picker case — Goal only carries
// component_id, so its owning program is resolved via a component lookup.
// `componentById`: Map(component_id -> component), built once by the caller
// from whichever components array it already has loaded (already true
// everywhere a Goal picker exists — a goal picker always sits alongside a
// component picker/filter).
export function buildGoalOptionGroups(goals, componentById, closedProgramIds) {
  return buildOwningProgramOptionGroups(
    goals, g => componentById.get(g.component_id)?.program_id, closedProgramIds,
    g => ({ value: String(g.id), label: g.goal_name || `Goal ${g.id}` }),
  );
}
